import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Target, Plus, Pencil, Trash2, X, Check, Send, ChevronDown, ChevronUp, Clock } from "lucide-react";
import { ConfirmDeleteDialog } from "@/components/ConfirmDeleteDialog";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusColors = {
  G: { dot: "bg-green-500", bg: "bg-green-50 dark:bg-green-900/30", text: "text-green-700 dark:text-green-300", label: "G - On Track" },
  Y: { dot: "bg-yellow-500", bg: "bg-yellow-50 dark:bg-yellow-900/30", text: "text-yellow-700 dark:text-yellow-300", label: "Y - At Risk" },
  R: { dot: "bg-red-500", bg: "bg-red-50 dark:bg-red-900/30", text: "text-red-700 dark:text-red-300", label: "R - Off Track" },
  N: { dot: "bg-slate-400", bg: "bg-slate-100 dark:bg-slate-700", text: "text-slate-600 dark:text-slate-300", label: "N - Not Started" },
  C: { dot: "bg-blue-500", bg: "bg-blue-50 dark:bg-blue-900/30", text: "text-blue-700 dark:text-blue-300", label: "C - Complete" },
};

const goalStatusColors = {
  "Not Started": "bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300",
  "Discovery": "bg-purple-50 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300",
  "In Development": "bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300",
  "UAT": "bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300",
  "Delivered": "bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300",
  "On Hold": "bg-orange-50 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300",
  "Cancelled": "bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300",
};

const strategicOptions = [
  "Priority 0 KLO",
  "Priority 1 Must Have",
  "Priority 2 Critical Operations",
  "Priority 3 New Enhancements",
];

const fmtDate = (d) => {
  if (!d) return "";
  const parts = d.split("-");
  if (parts.length === 3) return `${parts[1]}/${parts[2]}/${parts[0]}`;
  return d;
};

const emptyForm = {
  title: "", year: "", priority: 1, strategic_priority: "", overall_status: "N",
  goal_status: "Not Started", percent_complete: 0, goal_quarter: "",
  goal_type: "Required", start_date: "", target_end_date: "",
  actual_end_date: "", product_team: "",
};

export const GoalsPanel = () => {
  const { selectedProduct } = useAuth();
  const productId = selectedProduct?.id;
  const [goals, setGoals] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({ ...emptyForm });
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({});
  const [expandedId, setExpandedId] = useState(null);
  const [updateText, setUpdateText] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const params = productId ? { product_id: productId } : {};
      const [gRes, tRes] = await Promise.all([
        axios.get(`${API}/goals`, { params }),
        axios.get(`${API}/teams`),
      ]);
      setGoals(gRes.data);
      setTeams(tRes.data);
    } catch (err) { console.error(err); }
    setLoading(false);
  }, [productId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleCreate = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/goals`, { ...formData, title: selectedProduct?.name || formData.title, product_id: productId || "" });
      setFormData({ ...emptyForm });
      setShowForm(false);
      await fetchData();
    } catch (err) { console.error(err); }
    setSubmitting(false);
  };

  const startEdit = (goal) => {
    setEditingId(goal.id);
    setEditData({
      title: goal.title, year: goal.year || "", priority: goal.priority, strategic_priority: goal.strategic_priority,
      overall_status: goal.overall_status, goal_status: goal.goal_status,
      percent_complete: goal.percent_complete, goal_quarter: goal.goal_quarter,
      goal_type: goal.goal_type, start_date: goal.start_date,
      target_end_date: goal.target_end_date, actual_end_date: goal.actual_end_date,
      product_team: goal.product_team,
    });
  };

  const saveEdit = async (goalId) => {
    try {
      await axios.patch(`${API}/goals/${goalId}`, editData);
      setEditingId(null);
      await fetchData();
    } catch (err) { console.error(err); }
  };

  const deleteGoal = async (goalId) => {
    try { await axios.delete(`${API}/goals/${goalId}`); await fetchData(); }
    catch (err) { console.error(err); }
  };

  const addUpdate = async (goalId) => {
    if (!updateText.trim()) return;
    try {
      await axios.post(`${API}/goals/${goalId}/updates`, { text: updateText });
      setUpdateText("");
      await fetchData();
    } catch (err) { console.error(err); }
  };

  const deleteUpdate = async (goalId, updateId) => {
    try { await axios.delete(`${API}/goals/${goalId}/updates/${updateId}`); await fetchData(); }
    catch (err) { console.error(err); }
  };

  const set = (setter) => (key, val) => setter(prev => ({ ...prev, [key]: val }));
  const setF = set(setFormData);
  const setE = set(setEditData);

  const renderGoalForm = (data, onChange, onSubmit, onCancel, isEdit) => (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-xs font-medium text-slate-500 dark:text-slate-400 mb-1 block uppercase tracking-wider">Product</label>
          <Input value={selectedProduct?.name || ""} disabled className="bg-slate-50 dark:bg-slate-800 dark:border-slate-600 dark:text-slate-300" data-testid="goal-product-input" />
        </div>
        <div>
          <label className="text-xs font-medium text-slate-500 dark:text-slate-400 mb-1 block uppercase tracking-wider">Year</label>
          <Input value={data.year || ""} onChange={(e) => onChange("year", e.target.value)} placeholder="2026" className="dark:bg-slate-800 dark:border-slate-600 dark:text-slate-100" data-testid="goal-year-input" />
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <div>
          <label className="text-xs font-medium text-slate-500 dark:text-slate-400 mb-1 block uppercase tracking-wider">Goal Type</label>
          <Select value={data.goal_type} onValueChange={(v) => onChange("goal_type", v)}>
            <SelectTrigger className="dark:bg-slate-800 dark:border-slate-600 dark:text-slate-100"><SelectValue /></SelectTrigger>
            <SelectContent className="dark:bg-slate-800 dark:border-slate-700">
              <SelectItem value="Required" className="dark:text-slate-100">Required</SelectItem>
              <SelectItem value="Stretch" className="dark:text-slate-100">Stretch</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-xs font-medium text-slate-500 dark:text-slate-400 mb-1 block uppercase tracking-wider">Goal Quarter</label>
          <Select value={data.goal_quarter || "_none"} onValueChange={(v) => onChange("goal_quarter", v === "_none" ? "" : v)}>
            <SelectTrigger className="dark:bg-slate-800 dark:border-slate-600 dark:text-slate-100"><SelectValue placeholder="Select" /></SelectTrigger>
            <SelectContent className="dark:bg-slate-800 dark:border-slate-700">
              <SelectItem value="_none" className="dark:text-slate-100">--</SelectItem>
              {["Q1","Q2","Q3","Q4"].map(q => <SelectItem key={q} value={q} className="dark:text-slate-100">{q}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-xs font-medium text-slate-500 dark:text-slate-400 mb-1 block uppercase tracking-wider">% Complete</label>
          <Select value={String(data.percent_complete)} onValueChange={(v) => onChange("percent_complete", parseInt(v))}>
            <SelectTrigger className="dark:bg-slate-800 dark:border-slate-600 dark:text-slate-100"><SelectValue /></SelectTrigger>
            <SelectContent className="dark:bg-slate-800 dark:border-slate-700">
              {[0, 25, 50, 75, 100].map((v) => (
                <SelectItem key={v} value={String(v)} className="dark:text-slate-100">{v}%</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div>
          <label className="text-xs font-medium text-slate-500 dark:text-slate-400 mb-1 block uppercase tracking-wider">Strategic Priority</label>
          <Select value={data.strategic_priority || "_none"} onValueChange={(v) => onChange("strategic_priority", v === "_none" ? "" : v)}>
            <SelectTrigger className="dark:bg-slate-800 dark:border-slate-600 dark:text-slate-100 text-xs"><SelectValue placeholder="Select" /></SelectTrigger>
            <SelectContent className="dark:bg-slate-800 dark:border-slate-700">
              <SelectItem value="_none" className="dark:text-slate-100">--</SelectItem>
              {strategicOptions.map(s => <SelectItem key={s} value={s} className="dark:text-slate-100 text-xs">{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-xs font-medium text-slate-500 dark:text-slate-400 mb-1 block uppercase tracking-wider">Overall Status</label>
          <Select value={data.overall_status} onValueChange={(v) => onChange("overall_status", v)}>
            <SelectTrigger className="dark:bg-slate-800 dark:border-slate-600 dark:text-slate-100"><SelectValue /></SelectTrigger>
            <SelectContent className="dark:bg-slate-800 dark:border-slate-700">
              {Object.entries(statusColors).map(([key, cfg]) => (
                <SelectItem key={key} value={key} className="dark:text-slate-100">
                  <span className="flex items-center gap-2"><span className={`w-2 h-2 rounded-full ${cfg.dot}`} />{cfg.label}</span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-xs font-medium text-slate-500 dark:text-slate-400 mb-1 block uppercase tracking-wider">Goal Status</label>
          <Select value={data.goal_status} onValueChange={(v) => onChange("goal_status", v)}>
            <SelectTrigger className="dark:bg-slate-800 dark:border-slate-600 dark:text-slate-100"><SelectValue /></SelectTrigger>
            <SelectContent className="dark:bg-slate-800 dark:border-slate-700">
              {Object.keys(goalStatusColors).map(s => <SelectItem key={s} value={s} className="dark:text-slate-100">{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-xs font-medium text-slate-500 dark:text-slate-400 mb-1 block uppercase tracking-wider">Product Team</label>
          <Select value={data.product_team || "_none"} onValueChange={(v) => onChange("product_team", v === "_none" ? "" : v)}>
            <SelectTrigger className="dark:bg-slate-800 dark:border-slate-600 dark:text-slate-100"><SelectValue placeholder="Select team" /></SelectTrigger>
            <SelectContent className="dark:bg-slate-800 dark:border-slate-700">
              <SelectItem value="_none" className="dark:text-slate-100">--</SelectItem>
              {teams.map(t => <SelectItem key={t.id} value={t.name} className="dark:text-slate-100">{t.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div>
          <label className="text-xs font-medium text-slate-500 dark:text-slate-400 mb-1 block uppercase tracking-wider">Start Date</label>
          <Input type="date" value={data.start_date} onChange={(e) => onChange("start_date", e.target.value)} className="dark:bg-slate-800 dark:border-slate-600 dark:text-slate-100" />
        </div>
        <div>
          <label className="text-xs font-medium text-slate-500 dark:text-slate-400 mb-1 block uppercase tracking-wider">Target End Date</label>
          <Input type="date" value={data.target_end_date} onChange={(e) => onChange("target_end_date", e.target.value)} className="dark:bg-slate-800 dark:border-slate-600 dark:text-slate-100" />
        </div>
        <div>
          <label className="text-xs font-medium text-slate-500 dark:text-slate-400 mb-1 block uppercase tracking-wider">Actual End Date</label>
          <Input type="date" value={data.actual_end_date} onChange={(e) => onChange("actual_end_date", e.target.value)} className="dark:bg-slate-800 dark:border-slate-600 dark:text-slate-100" />
        </div>
      </div>
      <div className="flex gap-2 pt-1">
        <Button onClick={onSubmit} disabled={submitting} className="bg-[#00D4C8] hover:bg-[#00B8AE] text-white font-semibold">
          <Check className="w-4 h-4 mr-1" /> {isEdit ? "Save" : "Create Goal"}
        </Button>
        <Button variant="outline" onClick={onCancel} className="dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-700">Cancel</Button>
      </div>
    </div>
  );

  if (loading) return <div className="flex items-center justify-center py-20"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#00D4C8]" /></div>;

  return (
    <>
    <div className="space-y-6" data-testid="goals-panel">
      <div className="flex items-center justify-between">
        <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-slate-900 dark:text-slate-100 font-['Outfit']">Goals</h1>
      </div>

      {/* Goal cards */}
      <div className="space-y-4" data-testid="goals-list">
        {goals.map((goal) => {
          const sc = statusColors[goal.overall_status] || statusColors.N;
          const gsc = goalStatusColors[goal.goal_status] || goalStatusColors["Not Started"];
          const isExpanded = expandedId === goal.id;
          const isEditing = editingId === goal.id;

          return (
            <Card key={goal.id} className="card-modern overflow-hidden group" data-testid={`goal-card-${goal.id}`}>
              <div className={`h-1 ${sc.dot}`} />
              <CardContent className="p-5">
                {isEditing ? (
                  renderGoalForm(editData, setE, () => saveEdit(goal.id), () => setEditingId(null), true)
                ) : (
                  <>
                    <div className="flex items-start justify-between gap-3 mb-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          <h3 className="text-base font-semibold text-slate-800 dark:text-slate-100 font-['Outfit']">{goal.title}{goal.year ? ` — ${goal.year}` : ""}</h3>
                          <Badge className={`${gsc} border text-[11px] font-semibold`}>{goal.goal_status}</Badge>
                          <Badge className={`${sc.bg} ${sc.text} border text-[11px] font-semibold`}>
                            <span className={`w-1.5 h-1.5 rounded-full ${sc.dot} inline-block mr-1`} />{sc.label || goal.overall_status}
                          </Badge>
                        </div>
                        <div className="flex items-center gap-3 flex-wrap text-xs text-slate-500 dark:text-slate-400">
                          {goal.strategic_priority && <span>{goal.strategic_priority}</span>}
                          {goal.goal_quarter && <span className="font-semibold text-purple-600 dark:text-purple-400">{goal.goal_quarter}</span>}
                          {goal.product_team && <span>Team: {goal.product_team}</span>}
                          <span>{goal.goal_type}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button onClick={() => startEdit(goal)} className="p-1.5 rounded-md text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 transition-all" data-testid={`goal-edit-${goal.id}`}>
                          <Pencil className="w-3.5 h-3.5" />
                        </button>
                        <button onClick={() => setDeleteTarget({ id: goal.id, label: goal.title })} className="p-1.5 rounded-md text-slate-400 hover:text-red-500 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 transition-all" data-testid={`goal-delete-${goal.id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>

                    {/* Progress + Dates row */}
                    <div className="flex items-center gap-4 mb-3 flex-wrap">
                      <div className="flex items-center gap-2 flex-1 min-w-[200px]">
                        <div className="flex-1 h-2 bg-slate-100 dark:bg-slate-700 rounded-full overflow-hidden">
                          <div className={`h-full rounded-full ${goal.percent_complete >= 100 ? "bg-green-500" : goal.percent_complete >= 50 ? "bg-blue-500" : "bg-amber-500"} transition-all`} style={{ width: `${Math.min(goal.percent_complete, 100)}%` }} />
                        </div>
                        <span className="text-xs font-semibold text-slate-600 dark:text-slate-300 w-10 text-right">{goal.percent_complete}%</span>
                      </div>
                      <div className="flex items-center gap-3 text-[11px] text-slate-500 dark:text-slate-400">
                        {goal.start_date && <span>Start: {fmtDate(goal.start_date)}</span>}
                        {goal.target_end_date && <span>Target: {fmtDate(goal.target_end_date)}</span>}
                        {goal.actual_end_date && <span className="text-green-600 dark:text-green-400">Actual: {fmtDate(goal.actual_end_date)}</span>}
                      </div>
                    </div>

                    {/* Updates toggle */}
                    <button onClick={() => setExpandedId(isExpanded ? null : goal.id)} className="flex items-center gap-1.5 text-xs font-medium text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 transition-colors" data-testid={`goal-updates-toggle-${goal.id}`}>
                      <Clock className="w-3 h-3" /> Updates ({(goal.updates || []).length})
                      {isExpanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                    </button>

                    {isExpanded && (
                      <div className="mt-3 pt-3 border-t border-slate-100 dark:border-slate-700 space-y-2" data-testid={`goal-updates-${goal.id}`}>
                        {(goal.updates || []).slice().reverse().map((u) => (
                          <div key={u.id} className="flex items-start justify-between gap-2 p-2 rounded-md bg-slate-50 dark:bg-slate-800/60">
                            <div>
                              <p className="text-sm text-slate-700 dark:text-slate-200">{u.text}</p>
                              <p className="text-[10px] text-slate-400 dark:text-slate-500 mt-0.5">{u.created_by} — {new Date(u.created_at).toLocaleDateString()}</p>
                            </div>
                            <button onClick={() => deleteUpdate(goal.id, u.id)} className="text-slate-300 hover:text-red-500 dark:hover:text-red-400 flex-shrink-0 mt-0.5">
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        ))}
                        <div className="flex gap-2 mt-2">
                          <Input value={updateText} onChange={(e) => setUpdateText(e.target.value)} placeholder="Add an update..." className="text-sm dark:bg-slate-800 dark:border-slate-600 dark:text-slate-100" data-testid={`goal-update-input-${goal.id}`}
                            onKeyDown={(e) => { if (e.key === "Enter") { addUpdate(goal.id); } }} />
                          <Button onClick={() => addUpdate(goal.id)} size="sm" className="bg-[#00D4C8] hover:bg-[#00B8AE] text-white flex-shrink-0" data-testid={`goal-update-submit-${goal.id}`}>
                            <Send className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {goals.length === 0 && !showForm && (
        <div className="text-center py-12 text-slate-400 dark:text-slate-500">
          <Target className="w-10 h-10 mx-auto mb-3 opacity-40" />
          <p className="text-sm">No goals yet. Click below to add one.</p>
        </div>
      )}

      {showForm ? (
        <Card className="card-modern mt-4" data-testid="goal-create-form">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-100 font-['Outfit']">New Goal</h3>
              <button onClick={() => setShowForm(false)} className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"><X className="w-5 h-5" /></button>
            </div>
            {renderGoalForm(formData, setF, handleCreate, () => setShowForm(false), false)}
          </CardContent>
        </Card>
      ) : (
        <div>
          <Button onClick={() => setShowForm(true)} className="bg-[#00D4C8] hover:bg-[#00B8AE] text-white font-semibold" data-testid="add-goal-btn">
            <Plus className="w-4 h-4 mr-2" /> Add Goal
          </Button>
        </div>
      )}
    </div>
    <ConfirmDeleteDialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} onConfirm={() => deleteGoal(deleteTarget?.id)} title="Delete Goal" description={`Are you sure you want to delete "${deleteTarget?.label}"? This action cannot be undone.`} />
    </>
  );
};